import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Button from "@/v2-backoffice-client/components/button"
import { describeAppliedStep } from "@/v2-backoffice-client/layer/ai/layer.ai.steps"
import type { LayerAiAppliedStep } from "@/v2-backoffice-client/layer/ai/layer.ai.types"

export type LayerAiHistoryStepperProps = {
  steps: LayerAiAppliedStep[]
  /** How many steps are currently in effect. */
  cursor: number
  isBusy: boolean
  onGoToStep(target: number): void
}

/**
 * The application history as a timeline: one row per accepted suggestion
 * (or one per "Accept all" batch), summarizing what it changed in business
 * terms and whether it is currently in effect, connected by a rail through
 * the checkmarks. Clicking a row jumps the document to that point in time —
 * checked rows revert down to just before themselves, unchecked rows
 * re-apply up to and including themselves — replaying the recorded values,
 * so the editors re-sync even after manual undos in between. Rewind /
 * re-apply live in the header, usable even while the list is collapsed.
 */
function LayerAiHistoryStepper(props: LayerAiHistoryStepperProps) {
  const { steps, cursor, isBusy, onGoToStep } = props

  // Collapsed by default: the header's "N of M in effect" usually says
  // enough, and the vertical space belongs to the conversation.
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="flex shrink-0 flex-col gap-0.5 rounded-md border bg-muted/30 px-2 py-1.5">
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          aria-expanded={isExpanded}
          data-feature-code="UI_BackofficeLayers_AiAssistant_HistoryToggle"
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          <Icon src="time-past" size={11} className="text-muted-foreground" />
          <span className="text-[11px] font-medium">Applied changes</span>
          <span className="truncate text-[11px] text-muted-foreground">
            — {cursor} of {steps.length} in effect
          </span>
        </button>

        <Button
          size="tiny"
          variant="ghost"
          iconSrc="angle-small-left"
          disabled={cursor === 0 || isBusy}
          title="Rewind the most recent change"
          aria-label="Rewind the most recent change"
          data-feature-code="UI_BackofficeLayers_AiAssistant_StepBack"
          onClick={() => onGoToStep(cursor - 1)}
        />
        <Button
          size="tiny"
          variant="ghost"
          iconSrc="angle-small-right"
          disabled={cursor === steps.length || isBusy}
          title="Re-apply the next change"
          aria-label="Re-apply the next change"
          data-feature-code="UI_BackofficeLayers_AiAssistant_StepForward"
          onClick={() => onGoToStep(cursor + 1)}
        />
        <Button
          size="tiny"
          variant="ghost"
          iconSrc="angle-small-down"
          // Collapsed points up ("expands upward" — the list opens above
          // the composer), open points down; never sideways.
          iconClassName={cn(
            "transition-transform",
            !isExpanded && "rotate-180",
          )}
          title={isExpanded ? "Collapse the timeline" : "Expand the timeline"}
          aria-label={
            isExpanded ? "Collapse the timeline" : "Expand the timeline"
          }
          data-feature-code="UI_BackofficeLayers_AiAssistant_HistoryToggle"
          onClick={() => setIsExpanded((expanded) => !expanded)}
        />
      </div>

      {isExpanded && (
        <ul className="flex flex-col pt-0.5">
          {steps.map((step, index) => {
            const isInEffect = index < cursor
            const description = describeAppliedStep(step)
            return (
              <li
                key={index}
                className="relative flex gap-1.5 pb-1.5 last:pb-0"
              >
                {/* Timeline rail: the state dot, connected to the next
                    step's dot (never past the last row). */}
                <span className="relative flex w-4 shrink-0 justify-center">
                  <Icon
                    src={isInEffect ? "check-circle" : "circle"}
                    size={12}
                    className={cn(
                      "relative z-10 mt-1",
                      isInEffect ? "text-primary" : "text-muted-foreground",
                    )}
                  />
                  {index < steps.length - 1 && (
                    <span className="absolute bottom-[-2px] top-4 w-px bg-border" />
                  )}
                </span>

                <Button
                  variant="ghost"
                  size="tiny"
                  disabled={isBusy}
                  className={cn(
                    "min-w-0 flex-1 !justify-start font-normal",
                    !isInEffect && "text-muted-foreground",
                  )}
                  title={
                    isInEffect
                      ? `In effect (${description}) — click to rewind the document to just before this change`
                      : `Rewound (${description}) — click to re-apply the history up to and including this change`
                  }
                  data-feature-code="UI_BackofficeLayers_AiAssistant_StepJump"
                  onClick={() => onGoToStep(isInEffect ? index : index + 1)}
                >
                  <span className="truncate">{description}</span>
                  <span className="flex-1" />
                  <span className="shrink-0 text-[0.9em] text-muted-foreground">
                    {isInEffect ? "in effect" : "rewound"}
                  </span>
                </Button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default LayerAiHistoryStepper
